<template>
    <div
        class="flex cursor-pointer items-center gap-4 border p-4 hover:border-primary-500"
        :class="selected ? 'border-primary-800' : ''"
        :style="[
            selected
                ? 'box-shadow: 0 0 0 1px rgb(var(--color-primary-500));'
                : ''
        ]"
        @click="$emit('select')"
    >
        <radio-button
            :model-value="selected ? courseData.id : null"
            :input-id="courseData.id"
            :name="courseData.publicTitle"
            :aria-label="courseData.publicTitle"
            :value="courseData.id"
            @update:model-value="$event ? $emit('select') : void 0"
        />

        <div class="flex-1 space-y-1">
            <div class="flex flex-col md:flex-row">
                <div class="md:hidden">
                    <span
                        :class="{
                            'font-medium text-danger-600':
                                courseAvailabilityMessage.theme === 'danger',
                            'font-medium text-success-600':
                                courseAvailabilityMessage.theme === 'success',
                            italic: courseAvailabilityMessage.theme === 'base'
                        }"
                        >{{ courseAvailabilityMessage.message }}
                    </span>
                </div>
                <div class="flex-1">
                    <label
                        :for="courseData.id"
                        class="text-lg font-semibold"
                    >
                        {{ courseData.publicTitle }}
                    </label>
                    <div
                        v-if="courseData.subtitle !== ''"
                        class="pb-2 text-sm"
                    >
                        {{ courseData.subtitle }}
                    </div>
                </div>
                <div class="hidden md:block">
                    <span
                        :class="{
                            'font-medium text-danger-600':
                                courseAvailabilityMessage.theme === 'danger',
                            'font-medium text-success-600':
                                courseAvailabilityMessage.theme === 'success',
                            italic: courseAvailabilityMessage.theme === 'base'
                        }"
                        >{{ courseAvailabilityMessage.message }}
                    </span>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import { graphql } from '~/resources/graphql'

export const RecurringCourseRadioButtonCourseFragment = graphql(`
    fragment RecurringCourseRadioButtonCourseFragment on Course {
        id
        publicTitle
        subtitle
        capacityResult(filter: $capacityResultFilter) {
            enrollments
            overlappedBookedSessions
            contracts
            isWaitlistFull
        }
        bookingStatus
        bookingStatusAvailable(filter: $bookingStatusFilter) {
            customerName
            session {
                sessionId
                startDate
            }
            overrideCapacityConstraints
        }
        bookingPeriodAvailable(customerId: null, bookingDate: null)
    }
`)
</script>

<script setup lang="ts">
import RadioButton from 'primevue/radiobutton'
import { computed } from 'vue'
import type { FragmentType } from '~/resources/graphql'
import { getFragmentData } from '~/resources/graphql'
import { useCourseAvailabilityMessage } from '~/components/PurchaseContract/Program/helpers/use-course-availability-message'

const props = defineProps<{
    course: FragmentType<typeof RecurringCourseRadioButtonCourseFragment>
    selected: boolean
    availabilityThreshold: number | null
    waitlistEnabled: boolean
}>()

defineEmits<{
    (e: 'select'): void
}>()

const courseData = computed(() =>
    getFragmentData(RecurringCourseRadioButtonCourseFragment, props.course)
)

const courseAvailabilityMessage = useCourseAvailabilityMessage(
    computed(() => props.waitlistEnabled),
    computed(() => props.availabilityThreshold),
    computed(() => {
        return {
            bookingPeriodAvailable: courseData.value.bookingPeriodAvailable,
            bookingStatus: courseData.value.bookingStatus,
            bookingStatusAvailable: courseData.value.bookingStatusAvailable,
            capacityResult: {
                isWaitlistFull: courseData.value.capacityResult.isWaitlistFull,
                overlappedBookedSessions:
                    courseData.value.capacityResult.overlappedBookedSessions,
                contracts: courseData.value.capacityResult.contracts,
                enrollments: courseData.value.capacityResult.enrollments
            }
        }
    })
)
</script>
